import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useT } from "../contexts/LanguageContext";
import AppShell from "../components/app/AppShell";
import { toast } from "sonner";
import { FloppyDisk, Envelope, Plus, Trash, ShieldCheck } from "@phosphor-icons/react";
import PlansManager from "../components/admin/PlansManager";

const TABS = ["overview", "plans", "coupons", "about", "contact_info", "privacy", "kvkk", "terms", "distance_sales", "refund", "cookies", "faq", "social_links", "homepage_extra", "messages"];

export default function AdminPanel() {
  const { user, loading } = useAuth();
  const { t, lang } = useT();
  const navigate = useNavigate();
  const [tab, setTab] = useState("about");
  const [contents, setContents] = useState({});
  const [messages, setMessages] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!loading && (!user || !user.is_admin)) navigate("/dashboard", { replace: true });
  }, [user, loading, navigate]);

  const loadAll = async () => {
    try {
      const { data } = await api.get("/content");
      const map = Object.fromEntries(data.map((d) => [d.slug, d]));
      setContents(map);
    } catch { /* noop */ }
    try {
      const { data } = await api.get("/admin/contact-messages");
      setMessages(data);
    } catch { /* noop */ }
  };
  useEffect(() => { if (user?.is_admin) loadAll(); }, [user]); // eslint-disable-line

  const setField = (slug, path, value) => {
    setContents((prev) => {
      const next = { ...prev };
      const doc = { ...(next[slug] || { slug }) };
      const keys = path.split(".");
      let cur = doc;
      for (let i = 0; i < keys.length - 1; i++) {
        cur[keys[i]] = { ...(cur[keys[i]] || {}) };
        cur = cur[keys[i]];
      }
      cur[keys[keys.length - 1]] = value;
      next[slug] = doc;
      return next;
    });
  };

  const save = async (slug) => {
    setSaving(true);
    try {
      const doc = contents[slug] || {};
      await api.put(`/admin/content/${slug}`, {
        title: doc.title,
        body: doc.body,
        data: doc.data,
      });
      toast.success(t("admin.saved"));
      loadAll();
    } catch (e) {
      toast.error(t("admin.save_failed"));
    } finally {
      setSaving(false);
    }
  };

  if (loading || !user?.is_admin) {
    return <AppShell><div className="h-64 grid place-items-center"><div className="h-10 w-10 rounded-full border-2 border-slate-200 border-t-[#002FA7] animate-spin" /></div></AppShell>;
  }

  return (
    <AppShell>
      <div className="overline text-[#002FA7] flex items-center gap-1"><ShieldCheck size={12} weight="fill" /> ADMIN</div>
      <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-1">{t("admin.title")}</h1>
      <p className="text-slate-600 mt-2 max-w-xl">{t("admin.subtitle")}</p>

      <PretranslateWidget t={t} />

      <div className="mt-8 flex flex-wrap gap-2 border-b border-slate-200" data-testid="admin-tabs">
        {TABS.map((s) => (
          <button
            key={s}
            onClick={() => setTab(s)}
            data-testid={`admin-tab-${s}`}
            className={`px-3 py-2 text-sm font-medium border-b-2 transition-colors -mb-px ${tab === s ? "border-[#002FA7] text-[#002FA7]" : "border-transparent text-slate-500 hover:text-slate-900"}`}
          >
            {t(`admin.tab.${s}`)}
            {s === "messages" && messages.filter((m) => !m.read).length > 0 && (
              <span className="ml-1.5 inline-block px-1.5 py-0 rounded-full bg-[#E11D48] text-white text-[10px] font-mono">{messages.filter((m) => !m.read).length}</span>
            )}
          </button>
        ))}
      </div>

      <div className="mt-6">
        {tab === "overview" ? <OverviewTab t={t} lang={lang} /> :
         tab === "plans" ? <PlansManager /> :
         tab === "coupons" ? <CouponsTab t={t} lang={lang} /> :
         tab === "messages" ? <MessagesTab messages={messages} onRefresh={loadAll} t={t} /> :
         tab === "faq" ? <FAQEditor doc={contents.faq} onChange={(d) => setContents((p) => ({ ...p, faq: d }))} onSave={() => save("faq")} saving={saving} t={t} /> :
         tab === "social_links" ? <SocialEditor doc={contents.social_links} onChange={(d) => setContents((p) => ({ ...p, social_links: d }))} onSave={() => save("social_links")} saving={saving} t={t} /> :
         tab === "contact_info" ? <ContactInfoEditor doc={contents.contact_info} onChange={(d) => setContents((p) => ({ ...p, contact_info: d }))} onSave={() => save("contact_info")} saving={saving} t={t} /> :
         <ProseEditor slug={tab} doc={contents[tab]} setField={setField} onSave={() => save(tab)} saving={saving} t={t} lang={lang} />}
      </div>
    </AppShell>
  );
}

function ProseEditor({ slug, doc, setField, onSave, saving, t }) {
  return (
    <div className="space-y-5" data-testid={`prose-editor-${slug}`}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="overline">{t("admin.title_tr")}</label>
          <input value={doc?.title?.tr || ""} onChange={(e) => setField(slug, "title.tr", e.target.value)} data-testid={`field-title-tr-${slug}`} />
        </div>
        <div>
          <label className="overline">{t("admin.title_en")}</label>
          <input value={doc?.title?.en || ""} onChange={(e) => setField(slug, "title.en", e.target.value)} data-testid={`field-title-en-${slug}`} />
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="overline">{t("admin.body_tr")} <span className="text-[10px] text-slate-400 font-normal">(Markdown)</span></label>
          <textarea rows={20} value={doc?.body?.tr || ""} onChange={(e) => setField(slug, "body.tr", e.target.value)} className="!font-mono !text-xs" data-testid={`field-body-tr-${slug}`} />
        </div>
        <div>
          <label className="overline">{t("admin.body_en")} <span className="text-[10px] text-slate-400 font-normal">(Markdown)</span></label>
          <textarea rows={20} value={doc?.body?.en || ""} onChange={(e) => setField(slug, "body.en", e.target.value)} className="!font-mono !text-xs" data-testid={`field-body-en-${slug}`} />
        </div>
      </div>
      <div className="flex items-center gap-3">
        <button onClick={onSave} disabled={saving} className="btn-primary inline-flex items-center gap-2" data-testid={`save-${slug}`}>
          <FloppyDisk size={16} weight="bold" /> {saving ? t("admin.saving") : t("admin.save")}
        </button>
        <button
          onClick={async () => {
            if (!window.confirm(t("admin.reset_confirm"))) return;
            try {
              const { data: fresh } = await api.post(`/admin/content/${slug}/reset`);
              setField(slug, "title.tr", fresh.title?.tr || "");
              setField(slug, "title.en", fresh.title?.en || "");
              setField(slug, "body.tr", fresh.body?.tr || "");
              setField(slug, "body.en", fresh.body?.en || "");
              toast.success(t("admin.reset_success"));
            } catch {
              toast.error(t("admin.save_failed"));
            }
          }}
          className="btn-secondary !py-2 text-xs"
          data-testid={`reset-${slug}`}
        >
          {t("admin.reset_default")}
        </button>
      </div>
    </div>
  );
}

function SocialEditor({ doc, onChange, onSave, saving, t }) {
  const data = doc?.data || {};
  const set = (k, v) => onChange({ ...(doc || { slug: "social_links" }), data: { ...data, [k]: v } });
  const fields = [["twitter", "Twitter / X"], ["linkedin", "LinkedIn"], ["instagram", "Instagram"], ["youtube", "YouTube"], ["facebook", "Facebook"], ["github", "GitHub"]];
  return (
    <div className="space-y-4 max-w-2xl" data-testid="social-editor">
      <p className="text-sm text-slate-500">{t("admin.social_hint")}</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {fields.map(([k, label]) => (
          <div key={k}>
            <label className="overline">{label}</label>
            <input type="url" placeholder="https://..." value={data[k] || ""} onChange={(e) => set(k, e.target.value)} data-testid={`social-${k}-input`} />
          </div>
        ))}
      </div>
      <button onClick={onSave} disabled={saving} className="btn-primary inline-flex items-center gap-2" data-testid="save-social_links">
        <FloppyDisk size={16} weight="bold" /> {saving ? t("admin.saving") : t("admin.save")}
      </button>
    </div>
  );
}

function ContactInfoEditor({ doc, onChange, onSave, saving, t }) {
  const data = doc?.data || {};
  const set = (k, v) => onChange({ ...(doc || { slug: "contact_info" }), data: { ...data, [k]: v } });
  return (
    <div className="space-y-4 max-w-2xl" data-testid="contact-info-editor">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div><label className="overline">Email</label><input type="email" value={data.email || ""} onChange={(e) => set("email", e.target.value)} data-testid="ci-email" /></div>
        <div><label className="overline">Support Email</label><input type="email" value={data.support_email || ""} onChange={(e) => set("support_email", e.target.value)} data-testid="ci-support" /></div>
        <div><label className="overline">{t("contact.phone")}</label><input value={data.phone || ""} onChange={(e) => set("phone", e.target.value)} data-testid="ci-phone" /></div>
        <div><label className="overline">{t("admin.address_tr")}</label><input value={data.address_tr || ""} onChange={(e) => set("address_tr", e.target.value)} data-testid="ci-addr-tr" /></div>
        <div><label className="overline">{t("admin.address_en")}</label><input value={data.address_en || ""} onChange={(e) => set("address_en", e.target.value)} data-testid="ci-addr-en" /></div>
      </div>
      <button onClick={onSave} disabled={saving} className="btn-primary inline-flex items-center gap-2" data-testid="save-contact_info">
        <FloppyDisk size={16} weight="bold" /> {saving ? t("admin.saving") : t("admin.save")}
      </button>
    </div>
  );
}

function FAQEditor({ doc, onChange, onSave, saving, t }) {
  const items = doc?.data?.items || [];
  const setItems = (arr) => onChange({ ...(doc || { slug: "faq" }), data: { ...(doc?.data || {}), items: arr } });
  const update = (i, k, v) => setItems(items.map((it, j) => j === i ? { ...it, [k]: v } : it));
  const add = () => setItems([...items, { question_tr: "", question_en: "", answer_tr: "", answer_en: "" }]);
  const remove = (i) => setItems(items.filter((_, j) => j !== i));
  return (
    <div className="space-y-4" data-testid="faq-editor">
      {items.map((it, i) => (
        <div key={i} className="card space-y-3" data-testid={`faq-row-${i}`}>
          <div className="flex justify-between items-center">
            <div className="overline">Q{i + 1}</div>
            <button onClick={() => remove(i)} className="text-slate-400 hover:text-[#E11D48]" data-testid={`faq-remove-${i}`}><Trash size={16} /></button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div><label className="overline">{t("admin.question_tr")}</label><input value={it.question_tr} onChange={(e) => update(i, "question_tr", e.target.value)} data-testid={`faq-q-tr-${i}`} /></div>
            <div><label className="overline">{t("admin.question_en")}</label><input value={it.question_en} onChange={(e) => update(i, "question_en", e.target.value)} data-testid={`faq-q-en-${i}`} /></div>
            <div><label className="overline">{t("admin.answer_tr")}</label><textarea rows={3} value={it.answer_tr} onChange={(e) => update(i, "answer_tr", e.target.value)} data-testid={`faq-a-tr-${i}`} /></div>
            <div><label className="overline">{t("admin.answer_en")}</label><textarea rows={3} value={it.answer_en} onChange={(e) => update(i, "answer_en", e.target.value)} data-testid={`faq-a-en-${i}`} /></div>
          </div>
        </div>
      ))}
      <div className="flex gap-3">
        <button onClick={add} className="btn-secondary inline-flex items-center gap-2" data-testid="faq-add"><Plus size={16} weight="bold" /> {t("admin.add_question")}</button>
        <button onClick={onSave} disabled={saving} className="btn-primary inline-flex items-center gap-2" data-testid="save-faq"><FloppyDisk size={16} weight="bold" /> {saving ? t("admin.saving") : t("admin.save")}</button>
      </div>
    </div>
  );
}

function MessagesTab({ messages, onRefresh, t }) {
  const markRead = async (id) => {
    try { await api.post(`/admin/contact-messages/${id}/read`); onRefresh(); } catch {}
  };
  const remove = async (id) => {
    try { await api.delete(`/admin/contact-messages/${id}`); onRefresh(); toast.success(t("admin.deleted")); } catch {}
  };
  return (
    <div className="space-y-3" data-testid="messages-list">
      {messages.length === 0 && <div className="text-sm text-slate-500">{t("admin.no_messages")}</div>}
      {messages.map((m) => (
        <div key={m.message_id} className={`card ${!m.read ? "border-l-4 border-l-[#002FA7]" : ""}`} data-testid={`message-${m.message_id}`}>
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <Envelope size={14} className={!m.read ? "text-[#002FA7]" : "text-slate-400"} weight={!m.read ? "fill" : "regular"} />
                <span className="font-semibold text-sm">{m.name}</span>
                <span className="text-slate-400 text-xs">·</span>
                <a href={`mailto:${m.email}`} className="text-xs text-slate-500 hover:text-[#002FA7]">{m.email}</a>
              </div>
              {m.subject && <div className="text-sm font-medium mt-1">{m.subject}</div>}
              <div className="text-sm text-slate-600 mt-2 whitespace-pre-line">{m.message}</div>
              <div className="text-[10px] text-slate-400 mt-2 font-mono">{m.created_at}</div>
            </div>
            <div className="flex flex-col gap-2 shrink-0">
              {!m.read && <button onClick={() => markRead(m.message_id)} className="btn-secondary !py-1 !px-2 text-xs" data-testid={`mark-read-${m.message_id}`}>{t("admin.mark_read")}</button>}
              <button onClick={() => remove(m.message_id)} className="text-slate-400 hover:text-[#E11D48] self-end" data-testid={`delete-msg-${m.message_id}`}><Trash size={14} /></button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}


function PretranslateWidget({ t }) {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    try {
      const { data } = await api.get("/admin/pretranslate/status");
      setState(data);
    } catch { /* noop */ }
  };

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, 4000);
    return () => clearInterval(id);
  }, []);

  const start = async () => {
    setBusy(true);
    try {
      await api.post("/admin/pretranslate?lang=tr");
      toast.success(t("admin.pretranslate_started"));
      refresh();
    } catch {
      toast.error(t("admin.save_failed"));
    } finally {
      setBusy(false);
    }
  };

  if (!state) return null;

  const pct = state.total > 0 ? Math.round((state.done / state.total) * 100) : 0;
  const isRunning = state.running;
  const isComplete = !isRunning && state.total > 0 && state.done >= state.total;

  return (
    <div className="mt-6 card !p-4 flex flex-col sm:flex-row sm:items-center gap-4" data-testid="pretranslate-widget">
      <div className="flex-1">
        <div className="overline text-[#002FA7]">{t("admin.pretranslate_title")}</div>
        <div className="text-sm mt-1">
          {isRunning
            ? <>{t("admin.pretranslate_running")} <strong>{state.done}</strong> / <strong>{state.total}</strong> ({pct}%)</>
            : isComplete
              ? <span className="text-[#059669] font-medium">{t("admin.pretranslate_complete")}</span>
              : <span className="text-slate-500">{t("admin.pretranslate_idle")}</span>}
        </div>
        {isRunning && (
          <div className="mt-2 h-1.5 bg-slate-100 rounded-full overflow-hidden">
            <div className="h-full bg-[#002FA7] transition-all duration-500" style={{ width: `${pct}%` }} />
          </div>
        )}
      </div>
      {!isRunning && (
        <button onClick={start} disabled={busy} className="btn-secondary !py-2 text-sm" data-testid="pretranslate-start">
          {busy ? t("admin.saving") : t("admin.pretranslate_run")}
        </button>
      )}
    </div>
  );
}

function OverviewTab({ t, lang }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/admin/metrics/overview").then(({ data }) => setData(data)).catch(() => {});
  }, []);
  if (!data) return <div className="h-32 grid place-items-center"><div className="h-8 w-8 rounded-full border-2 border-slate-200 border-t-[#002FA7] animate-spin" /></div>;
  const fmt = (n) => Number(n || 0).toLocaleString(lang === "tr" ? "tr-TR" : "en-US");
  const fmtTry = (n) => `${Number(n || 0).toLocaleString(lang === "tr" ? "tr-TR" : "en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}₺`;

  const cards = [
    { label: t("overview.total_users"), value: fmt(data.users.total), sub: `${t("overview.new")} 30g: ${fmt(data.users.new_30d)}`, testid: "overview-total-users" },
    { label: t("overview.paying"), value: fmt(data.users.paying), sub: `${data.subscriptions.conversion_rate_pct}% ${t("overview.conversion")}`, testid: "overview-paying" },
    { label: t("overview.mrr"), value: fmtTry(data.revenue.mrr_try), sub: t("overview.monthly_recurring"), testid: "overview-mrr" },
    { label: t("overview.revenue_30d"), value: fmtTry(data.revenue.revenue_30d_try), sub: `${t("overview.total")}: ${fmtTry(data.revenue.total_try)}`, testid: "overview-revenue-30d" },
    { label: t("overview.affiliates"), value: fmt(data.users.affiliates), sub: t("overview.active"), testid: "overview-affiliates" },
    { label: t("overview.active_coupons"), value: fmt(data.coupons.active), sub: `${fmt(data.coupons.total_redemptions)} ${t("overview.redemptions")}`, testid: "overview-coupons" },
    { label: t("overview.unread_msg"), value: fmt(data.support.unread_messages), sub: t("overview.contact_forms"), testid: "overview-messages" },
    { label: t("overview.pro_active"), value: fmt(data.subscriptions.pro_active), sub: `Premium: ${fmt(data.subscriptions.master_active)}`, testid: "overview-pro-active" },
  ];

  return (
    <div className="space-y-6" data-testid="overview-tab">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {cards.map((c) => (
          <div key={c.label} className="card !p-4" data-testid={c.testid}>
            <div className="overline">{c.label}</div>
            <div className="font-display text-2xl font-bold mt-1">{c.value}</div>
            <div className="text-xs text-slate-500 mt-1">{c.sub}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="card">
          <div className="overline">{t("overview.recent_payments")}</div>
          {data.recent_payments.length === 0 ? (
            <p className="text-sm text-slate-500 mt-3">{t("overview.no_payments_yet")}</p>
          ) : (
            <table className="w-full text-sm mt-3" data-testid="overview-recent-payments-table">
              <tbody>
                {data.recent_payments.map((p) => (
                  <tr key={p.conversation_id} className="border-b border-slate-100">
                    <td className="py-2 text-xs text-slate-600">{new Date(p.completed_at).toLocaleDateString(lang === "tr" ? "tr-TR" : "en-US")}</td>
                    <td className="py-2 capitalize">{p.plan}</td>
                    <td className="py-2 text-right font-bold text-[#002FA7]">{fmtTry(p.gross)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <div className="card">
          <div className="overline">{t("overview.recent_signups")}</div>
          <table className="w-full text-sm mt-3" data-testid="overview-recent-signups-table">
            <tbody>
              {data.recent_users.map((u) => (
                <tr key={u.user_id} className="border-b border-slate-100">
                  <td className="py-2 text-xs text-slate-600">{new Date(u.created_at).toLocaleDateString(lang === "tr" ? "tr-TR" : "en-US")}</td>
                  <td className="py-2 font-mono text-xs">{u.email}</td>
                  <td className="py-2 text-right">
                    <span className={`text-xs px-2 py-0.5 rounded ${u.plan === "free" ? "bg-slate-100" : "bg-emerald-50 text-emerald-700"}`}>{u.plan}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {data.top_affiliates.length > 0 && (
        <div className="card">
          <div className="overline">{t("overview.top_affiliates")}</div>
          <table className="w-full text-sm mt-3" data-testid="overview-top-affiliates-table">
            <tbody>
              {data.top_affiliates.map((a) => (
                <tr key={a._id} className="border-b border-slate-100">
                  <td className="py-2 font-mono text-xs">{a._id}</td>
                  <td className="py-2">{a.count} {t("overview.commissions")}</td>
                  <td className="py-2 text-right font-bold text-[#002FA7]">{fmtTry(a.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function CouponsTab({ t, lang }) {
  const [coupons, setCoupons] = useState([]);
  const [form, setForm] = useState({ code: "", discount_type: "percent", discount_value: 20, applicable_plans: [], usage_limit: "", per_user_limit: 1, valid_until: "", note: "" });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/coupons/list");
      setCoupons(data);
    } catch {}
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    setBusy(true);
    try {
      const payload = { ...form };
      payload.code = payload.code.trim().toUpperCase();
      payload.discount_value = Number(payload.discount_value);
      payload.usage_limit = payload.usage_limit ? Number(payload.usage_limit) : null;
      payload.per_user_limit = Number(payload.per_user_limit) || 1;
      if (!payload.valid_until) payload.valid_until = null;
      await api.post("/admin/coupons/create", payload);
      toast.success(t("admin.coupon_created"));
      setForm({ code: "", discount_type: "percent", discount_value: 20, applicable_plans: [], usage_limit: "", per_user_limit: 1, valid_until: "", note: "" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || t("admin.save_failed"));
    } finally {
      setBusy(false);
    }
  };

  const toggleActive = async (code, active) => {
    try {
      await api.patch(`/admin/coupons/${code}`, { active: !active });
      load();
    } catch {}
  };

  const remove = async (code) => {
    if (!window.confirm(t("admin.coupon_delete_confirm", { code }))) return;
    try {
      await api.delete(`/admin/coupons/${code}`);
      toast.success(t("admin.deleted"));
      load();
    } catch {}
  };

  return (
    <div className="space-y-6" data-testid="coupons-tab">
      <div className="card">
        <div className="overline">{t("admin.coupon_new")}</div>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
          <input placeholder="KOD (örn: WELCOME20)" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} data-testid="coupon-form-code" />
          <select value={form.discount_type} onChange={(e) => setForm({ ...form, discount_type: e.target.value })} data-testid="coupon-form-type" className="px-3 py-2 rounded-lg border border-slate-300">
            <option value="percent">{t("admin.coupon_type_percent")}</option>
            <option value="fixed">{t("admin.coupon_type_fixed")}</option>
          </select>
          <input type="number" min="1" placeholder={form.discount_type === "percent" ? "% 20" : "50 ₺"} value={form.discount_value} onChange={(e) => setForm({ ...form, discount_value: e.target.value })} data-testid="coupon-form-value" />
          <input type="number" min="1" placeholder={t("admin.coupon_per_user_limit")} value={form.per_user_limit} onChange={(e) => setForm({ ...form, per_user_limit: e.target.value })} data-testid="coupon-form-per-user" />
          <input type="number" min="1" placeholder={t("admin.coupon_total_limit")} value={form.usage_limit} onChange={(e) => setForm({ ...form, usage_limit: e.target.value })} data-testid="coupon-form-total-limit" />
          <input type="date" value={form.valid_until} onChange={(e) => setForm({ ...form, valid_until: e.target.value ? new Date(e.target.value).toISOString() : "" })} data-testid="coupon-form-valid-until" />
        </div>
        <div className="mt-3">
          <label className="text-sm text-slate-600">{t("admin.coupon_plans")}:&nbsp;</label>
          {["pro", "master"].map((p) => (
            <label key={p} className="inline-flex items-center gap-1 mr-3 text-sm">
              <input type="checkbox" checked={form.applicable_plans.includes(p)} onChange={(e) => {
                const next = e.target.checked ? [...form.applicable_plans, p] : form.applicable_plans.filter((x) => x !== p);
                setForm({ ...form, applicable_plans: next });
              }} />
              <span className="capitalize">{p}</span>
            </label>
          ))}
          <span className="text-xs text-slate-500 ml-2">({t("admin.coupon_empty_means_all")})</span>
        </div>
        <input className="mt-3 w-full" placeholder={t("admin.coupon_note")} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="coupon-form-note" />
        <button onClick={create} disabled={busy || !form.code} className="mt-3 btn-primary disabled:opacity-50" data-testid="coupon-create-submit">
          {busy ? t("admin.saving") : t("admin.coupon_create")}
        </button>
      </div>

      <div className="card">
        <div className="overline">{t("admin.coupon_list")}</div>
        {coupons.length === 0 ? (
          <p className="text-sm text-slate-500 mt-3">{t("admin.coupon_none")}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm mt-3" data-testid="coupon-list-table">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200">
                  <th className="py-2 pr-3">{t("admin.coupon_code")}</th>
                  <th className="py-2 pr-3">{t("admin.coupon_discount")}</th>
                  <th className="py-2 pr-3">{t("admin.coupon_used")}</th>
                  <th className="py-2 pr-3">{t("admin.coupon_status")}</th>
                  <th className="py-2 pr-3"></th>
                </tr>
              </thead>
              <tbody>
                {coupons.map((c) => (
                  <tr key={c.code} className="border-b border-slate-100">
                    <td className="py-2 pr-3 font-mono font-bold">{c.code}</td>
                    <td className="py-2 pr-3">{c.discount_type === "percent" ? `%${c.discount_value}` : `${c.discount_value}₺`}{c.applicable_plans?.length > 0 && <span className="text-xs text-slate-500"> · {c.applicable_plans.join(",")}</span>}</td>
                    <td className="py-2 pr-3">{c.used_count}{c.usage_limit && ` / ${c.usage_limit}`}</td>
                    <td className="py-2 pr-3">
                      <button onClick={() => toggleActive(c.code, c.active)} className={`text-xs px-2 py-0.5 rounded ${c.active ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`} data-testid={`coupon-toggle-${c.code}`}>
                        {c.active ? t("admin.coupon_active") : t("admin.coupon_inactive")}
                      </button>
                    </td>
                    <td className="py-2 pr-3 text-right">
                      <button onClick={() => remove(c.code)} className="text-xs text-red-600 hover:text-red-800" data-testid={`coupon-delete-${c.code}`}>
                        {t("admin.delete")}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

